/**
 * Copy a fresh one-visit tech link (#1096).
 * The bearer stays in memory only long enough to reach the clipboard. Toasts name the technician, never the URL.
 */
import { Button, MenuItem, toast } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import { describeError, unwrap } from '../../lib/api/client'
import type { VisitAssigneeOut, VisitOut } from '../../lib/api/generated'
import { techLinkApiV1VisitsVisitIdTechLinkPost, techLinkTargetsApiV1CasesCaseIdTechLinkTargetsGet } from '../../lib/api/generated'
import { sessionQuery } from '../../lib/api/queries'
import { formatWhen } from '../../lib/display-time'

export function internalTechAssignees(visit: VisitOut): VisitAssigneeOut[] {
  // A just-booked VisitOut from the create response can omit assignees. Treat that as none.
  return (visit.assignees ?? []).filter(
    (assignee) => !assignee.calendar_deleted_at && assignee.kind === 'internal_user' && (assignee.user_id || assignee.desk_agent_id),
  )
}

export function techLinkLabel(assignee: { display_internal?: string | null }): string {
  const name = assignee.display_internal?.trim()
  return name || 'technician'
}

function expiryLabel(value: string | null | undefined): string {
  if (!value) return 'later'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'later'
  return formatWhen(date.toISOString(), { style: 'absolute' })
}

export function useDispatcherSession(): boolean {
  const session = useQuery({ ...sessionQuery(), enabled: false })
  const role = session.data?.signed_in ? session.data.me?.role : null
  return role === 'manager' || role === 'cover'
}

/** Mint, or say nothing is outstanding. The URL is copied and then dropped. */
export async function copyTechLink(visitId: string, assigneeId: string, name: string): Promise<void> {
  let result: { minted: boolean; url?: string | null; expires_at?: string | null; recipient_name: string; message: string }
  try {
    result = await unwrap(
      techLinkApiV1VisitsVisitIdTechLinkPost({
        path: { visit_id: visitId },
        body: { assignee_id: assigneeId, initiated_by: 'human' },
      }),
    )
  } catch (error) {
    toast.error(describeError(error))
    return
  }
  const who = result.recipient_name || name
  if (!result.minted || !result.url) {
    toast(result.message || `Nothing is outstanding for ${who} on this visit.`)
    return
  }
  const when = expiryLabel(result.expires_at)
  try {
    await navigator.clipboard.writeText(result.url)
    toast.success(`Link for ${who} copied. Expires ${when}.`)
  } catch {
    toast(`Link for ${who} was issued. The clipboard is unavailable.`)
  }
}

export function TechLinkCopies({ visit }: { visit: VisitOut }) {
  const allowed = useDispatcherSession()
  const techs = internalTechAssignees(visit)
  if (!allowed || techs.length === 0) return null
  return (
    <div data-testid="copy-tech-links" className="flex flex-wrap gap-2">
      {techs.map((assignee) => {
        const name = techLinkLabel(assignee)
        return (
          <Button
            key={assignee.id}
            type="button"
            size="sm"
            variant="outline"
            data-testid="copy-tech-link"
            onClick={() => void copyTechLink(visit.id, assignee.id, name)}
          >
            Copy tech link · {name}
          </Button>
        )
      })}
    </div>
  )
}

export function useTechLinkTargets(caseId: string, active: boolean) {
  const allowed = useDispatcherSession()
  return {
    allowed,
    query: useQuery({
      queryKey: ['tech-link-targets', caseId],
      queryFn: () => unwrap(techLinkTargetsApiV1CasesCaseIdTechLinkTargetsGet({ path: { case_id: caseId } })),
      enabled: active && allowed,
      staleTime: 15_000,
    }),
  }
}

function targetLabel(name: string): string {
  return `Copy tech link · ${name}`
}

export function TechReportMenuItems({ caseId, active }: { caseId: string; active: boolean }) {
  const { allowed, query } = useTechLinkTargets(caseId, active)
  if (!allowed) return null
  if (!active && !query.data) return null
  if (query.isPending) return <MenuItem disabled>Copy tech link…</MenuItem>
  if (query.isError) return <MenuItem disabled>Tech links unavailable</MenuItem>
  const targets = query.data?.targets ?? []
  if (targets.length === 0) return <MenuItem disabled>Nothing outstanding for a technician</MenuItem>
  return targets.map((target) => (
    <MenuItem
      key={`${target.visit_id}:${target.assignee_id}`}
      data-testid="copy-tech-link"
      onClick={() => void copyTechLink(target.visit_id, target.assignee_id, target.name)}
    >
      {targetLabel(target.name)}
    </MenuItem>
  ))
}

export function TechReportActionButtons({ caseId, active }: { caseId: string; active: boolean }) {
  const { allowed, query } = useTechLinkTargets(caseId, active)
  if (!allowed) return null
  if (query.isPending) {
    return (
      <Button size="sm" variant="outline" disabled>
        Copy tech link…
      </Button>
    )
  }
  if (query.isError) {
    return (
      <Button size="sm" variant="outline" disabled>
        Tech links unavailable
      </Button>
    )
  }
  const targets = query.data?.targets ?? []
  if (targets.length === 0) {
    return (
      <Button size="sm" variant="outline" disabled>
        Nothing outstanding for a technician
      </Button>
    )
  }
  return targets.map((target) => (
    <Button
      key={`${target.visit_id}:${target.assignee_id}`}
      size="sm"
      variant="outline"
      data-testid="copy-tech-link"
      onClick={() => void copyTechLink(target.visit_id, target.assignee_id, target.name)}
    >
      {targetLabel(target.name)}
    </Button>
  ))
}
